// src/hooks/usePermissions.js - VERSION COMPLÈTE AMÉLIORÉE
import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { api } from '../services/api';

// Contexte pour les permissions
const PermissionsContext = createContext(null);

// ✅ NOUVEAU : Variable globale pour empêcher les appels multiples
let permissionsPromise = null;

// Hook pour utiliser les permissions
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions doit être utilisé à l\'intérieur d\'un PermissionsProvider');
  }
  return context;
};

// Provider qui gère l'état des permissions
export const PermissionsProvider = ({ children }) => {
  const [modulePermissions, setModulePermissions] = useState(new Set());
  const [detailedPermissions, setDetailedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // ✅ Cache avec timestamp pour éviter les appels répétés
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const CACHE_KEY = 'userPermissions';
  const CACHE_TIMESTAMP_KEY = 'permissionsTimestamp';

  // ✅ AMÉLIORÉ : Fonction pour vider le cache avec reset des promesses
  const clearPermissionsCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem('lastToken'); // ✅ NOUVEAU
      permissionsPromise = null; // ✅ CRITIQUE : Reset la promesse globale
      console.log('🗑️ Cache des permissions vidé complètement');
    } catch (error) {
      console.warn('⚠️ Erreur lors du nettoyage du cache:', error);
    }
  }, []);

  // ✅ Fonction pour sauvegarder en cache
  const cachePermissions = useCallback((permissions) => {
    try {
      if (!permissions || permissions.length === 0) {
        console.warn('⚠️ Tentative de mise en cache de permissions vides - ignoré');
        return;
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(permissions));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('💾 Permissions mises en cache:', permissions.length, 'permissions');
    } catch (error) {
      console.warn('⚠️ Erreur lors de la mise en cache:', error);
    }
  }, []);

  // ✅ Fonction pour récupérer du cache
  const getCachedPermissions = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (!cachedData || !timestamp) {
        return null;
      }
      
      const age = Date.now() - parseInt(timestamp);
      if (age > CACHE_DURATION) {
        console.log('⏰ Cache expiré, suppression...');
        clearPermissionsCache();
        return null;
      }
      
      const permissions = JSON.parse(cachedData);
      if (!Array.isArray(permissions) || permissions.length === 0) {
        console.log('📦 Cache vide ou corrompu, suppression...');
        clearPermissionsCache();
        return null;
      }
      
      console.log('📦 Permissions récupérées du cache:', permissions.length, 'permissions');
      return permissions;
    } catch (error) {
      console.warn('⚠️ Erreur lors de la lecture du cache:', error);
      clearPermissionsCache();
      return null;
    }
  }, [clearPermissionsCache]);

  // ✅ FONCTION PRINCIPALE AMÉLIORÉE : Protection contre race conditions + structure adaptée
  const loadUserPermissions = useCallback(async (forceRefresh = false) => {
    try {
      console.log('🔍 Début du chargement des permissions...', { forceRefresh });
      
      const token = localStorage.getItem('token');
      console.log('🎫 Token récupéré:', token ? 'Présent' : 'Absent');
      
      if (!token) {
        console.log('❌ Pas de token, réinitialisation des permissions');
        setModulePermissions(new Set());
        setDetailedPermissions([]);
        clearPermissionsCache();
        setLoading(false);
        permissionsPromise = null;
        return;
      }

      // ✅ CRITIQUE : Empêcher les appels multiples simultanés
      if (permissionsPromise && !forceRefresh) {
        console.log('⏳ Appel déjà en cours, réutilisation de la promesse existante...');
        await permissionsPromise;
        return;
      }

      // ✅ NOUVEAU : Détecter les changements de token
      const lastToken = localStorage.getItem('lastToken');
      if (lastToken !== token) {
        console.log('🔄 Nouveau token détecté, vidage du cache...');
        clearPermissionsCache();
        localStorage.setItem('lastToken', token);
        forceRefresh = true;
      }

      // ✅ Vérifier le cache si pas de refresh forcé
      if (!forceRefresh) {
        const cachedPermissions = getCachedPermissions();
        if (cachedPermissions) {
          // ✅ CORRIGÉ : Extraire les modules avec ACTION = 'ACCESS'
          const userModules = new Set(
            cachedPermissions
              .filter(p => p.action === 'ACCESS')
              .map(p => p.module)
          );
          setDetailedPermissions(cachedPermissions);
          setModulePermissions(userModules);
          setLoading(false);
          return;
        }
      }

      console.log('🌐 Appel API pour récupérer les permissions...');
      
      // ✅ CRITIQUE : Créer une seule promesse partagée avec timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout API')), 10000)
      );
      
      permissionsPromise = Promise.race([
        api.auth.getMyPermissions(),
        timeoutPromise
      ]);
      
      const response = await permissionsPromise;
      
      console.log('📊 Réponse API complète:', response);
      console.log('📊 Type de response.data:', typeof response.data, Array.isArray(response.data));
      console.log('📊 Contenu response.data:', response.data);
      
      // ✅ NOUVEAU CODE (à insérer)
  if (response && response.success && response.data) {
    // ✅ CORRECTION : Les permissions sont dans response.data.rolePermissions
    const rolePermissions = response.data.rolePermissions || [];
    
    console.log('✅ Réponse API valide avec', rolePermissions.length, 'permissions');
    console.log('📊 Structure première permission:', rolePermissions[0]);
    console.log('📊 Toutes les permissions:', rolePermissions);
    
    // ✅ CORRIGÉ : Extraire correctement les modules avec ACCESS
    const userModules = new Set(
      rolePermissions
        .filter(p => p.action === 'ACCESS')
        .map(p => p.module)
    );
        
        console.log('✅ Modules avec ACCESS extraits:', [...userModules]);
        
        setDetailedPermissions(response.data);
        setModulePermissions(userModules);
        setLastUpdate(new Date());
        
        // Mettre en cache seulement si les données sont valides
        if (response.data.length > 0) {
          cachePermissions(response.data);
        }
        
        setError(null);
      } else {
        console.error('❌ Réponse API invalide:', {
          hasResponse: !!response,
          hasSuccess: response?.success,
          isDataArray: Array.isArray(response?.data),
          dataType: typeof response?.data,
          dataContent: response?.data
        });
        setError('Réponse API invalide');
        
        // ✅ NOUVEAU : Fallback vers le cache en cas d'erreur
        const fallbackCache = getCachedPermissions();
        if (fallbackCache) {
          console.log('🆘 Utilisation du cache de secours');
          const userModules = new Set(
            fallbackCache
              .filter(p => p.action === 'ACCESS')
              .map(p => p.module)
          );
          setDetailedPermissions(fallbackCache);
          setModulePermissions(userModules);
          setError(null);
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des permissions:', error);
      setError(error.message);
      
      // ✅ NOUVEAU : En cas d'erreur réseau, utiliser le cache
      if (error.message === 'Timeout API' || error.name === 'NetworkError') {
        const fallbackCache = getCachedPermissions();
        if (fallbackCache) {
          console.log('🆘 Erreur réseau - Utilisation du cache de secours');
          const userModules = new Set(
            fallbackCache
              .filter(p => p.action === 'ACCESS')
              .map(p => p.module)
          );
          setDetailedPermissions(fallbackCache);
          setModulePermissions(userModules);
          setError(null);
        }
      }
    } finally {
      console.log('🏁 Fin du chargement, loading = false');
      setLoading(false);
      permissionsPromise = null; // ✅ Reset pour permettre futurs appels
    }
  }, [getCachedPermissions, cachePermissions, clearPermissionsCache]);

  // ✅ CORRIGÉ : hasPermission adapté à ta structure API
  // ⚠️ Ne PAS réintroduire de raccourci ALL_MODULES ici.
  //
  // Ce hook répondait « oui » à toute question dès qu'il voyait
  // `ALL_MODULES.ACCESS`, sans regarder la liste. Deux conséquences : le menu
  // promettait des modules que le serveur refusait, et une dérogation
  // « refuser » ne pouvait pas atteindre un porteur du passe-partout — le
  // module refusé restait affiché.
  //
  // Depuis le 4 septembre 2026, `getUserPermissionsV2` développe ALL_MODULES
  // côté serveur et en retire les refus. La liste reçue est donc déjà la vérité :
  // il suffit de la lire.
  const hasPermission = useCallback((module) => {
    const result = detailedPermissions.some(p =>
      p.module === module && p.action === 'ACCESS'
    );

    console.log(`🔍 hasPermission(${module}):`, result);
    return result;
  }, [detailedPermissions]);

  // ✅ CORRIGÉ : hasGranularPermission adapté à ta structure
  // ✅ FONCTION CORRIGÉE : hasGranularPermission avec logique hybride
const hasGranularPermission = useCallback((module, sousModule, action) => {
  console.log(`🔍 hasGranularPermission(${module}, ${sousModule}, ${action})`);
  
  if (!detailedPermissions || detailedPermissions.length === 0) {
    console.log('❌ Aucune permission détaillée disponible');
    return false;
  }

  // Pas de raccourci ALL_MODULES : le serveur l'a déjà développé dans la liste,
  // refus individuels retirés. Le rétablir ici rouvrirait ce que le serveur ferme.

  // CAS 2: NOUVEAU - Vérifier si c'est une demande d'ACCESS au module
  if (action === 'ACCESS' && !sousModule) {
    // Pour les demandes MODULE.ACCESS, utiliser la logique hybride
    const hasModuleAccess = detailedPermissions.some(p => 
      p.module === module && p.action === 'ACCESS'
    );
    
    // Si il n'a pas MODULE.ACCESS, vérifier s'il a au moins une permission dans ce module
    if (!hasModuleAccess) {
      const hasAnyPermissionInModule = detailedPermissions.some(p => p.module === module);
      console.log(`🚪 Logique hybride ${module}.ACCESS:`, hasAnyPermissionInModule ? 'AUTORISÉ' : 'REFUSÉ');
      return hasAnyPermissionInModule;
    }
    
    console.log(`✅ Permission ${module}.ACCESS trouvée directement`);
    return true;
  }

  // CAS 3: CORRIGÉ - Pour les actions spécifiques, vérifier la permission exacte
  // NE PAS autoriser automatiquement toutes les actions si l'utilisateur a ACCESS
  
  // Construire la clé de permission
  const permissionKey = sousModule 
    ? `${module}.${sousModule}.${action}`
    : `${module}.${action}`;
  
  // Chercher la permission spécifique
  const hasSpecificPermission = detailedPermissions.some(p => {
    // Vérifier par clé exacte
    if (p.key === permissionKey) return true;
    
    // Vérifier par structure module/sous_module/action
    const moduleMatch = p.module === module;
    const sousModuleMatch = sousModule ? p.sous_module === sousModule : !p.sous_module;
    const actionMatch = p.action === action;
    
    return moduleMatch && sousModuleMatch && actionMatch;
  });

  if (hasSpecificPermission) {
    console.log(`✅ Permission spécifique trouvée: ${permissionKey}`);
    return true;
  }

  // CAS 4: NOUVEAU - Vérifier les permissions par défaut (is_default = 1)
  const hasDefaultPermission = detailedPermissions.some(p => 
    p.module === module && 
    (sousModule ? p.sous_module === sousModule : true) && 
    p.action === action && 
    p.is_default === 1
  );

  if (hasDefaultPermission) {
    console.log(`✅ Permission par défaut trouvée: ${permissionKey}`);
    return true;
  }

  console.log(`❌ Permission refusée pour: ${permissionKey}`);
  console.log('📋 Permissions disponibles:', detailedPermissions.map(p => 
    `${p.module}${p.sous_module ? '.' + p.sous_module : ''}.${p.action}${p.key ? ' (key: ' + p.key + ')' : ''}`
  ));
  
  return false;
}, [detailedPermissions]);


  // ✅ NOUVEAU : Fonction de debug spécifique à ta structure
  const debugUserPermissions = useCallback(() => {
    console.log('=== DEBUG PERMISSIONS STRUCTURE ===');
    console.log('Raw permissions from API:', detailedPermissions);
    console.log('Modules with ACCESS:', detailedPermissions
      .filter(p => p.action === 'ACCESS')
      .map(p => p.module)
    );
    console.log('All actions:', [...new Set(detailedPermissions.map(p => p.action))]);
    console.log('ModulePermissions Set:', [...modulePermissions]);
    console.log('Loading:', loading);
    console.log('Error:', error);
    console.log('================================');
    
    // Test des permissions importantes
    const testModules = ['ELEVES', 'RESSOURCE_HUMAINES', 'CLASSE_ET_MATIERES', 'ALL_MODULES'];
    testModules.forEach(module => {
      console.log(`Test ${module}:`, hasPermission(module));
    });
    
    return {
      loading,
      detailedPermissions,
      modulePermissions: [...modulePermissions],
      error,
      lastUpdate,
      cache: getCachedPermissions()
    };
  }, [detailedPermissions, modulePermissions, loading, error, lastUpdate, hasPermission, getCachedPermissions]);

  // ✅ Fonction pour forcer le rafraîchissement
  // Rafraîchissement SILENCIEUX : `loading` ne repasse pas à true. Sinon
  // ProtectedRoute (et tout consommateur) remplaçait la page par sa roue, la
  // démontait, et « Outils administratifs » retombait sur l'onglet Paramètres
  // à chaque permission enregistrée (18 septembre 2026). Les droits déjà
  // chargés restent en vigueur le temps de l'appel.
  const refreshPermissions = useCallback(async () => {
    console.log('🔄 Rafraîchissement forcé des permissions...');
    await loadUserPermissions(true);
  }, [loadUserPermissions]);

  // ✅ Effet pour écouter les changements de token
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          // Token supprimé = déconnexion
          console.log('🚪 Déconnexion détectée, nettoyage des permissions');
          setModulePermissions(new Set());
          setDetailedPermissions([]);
          clearPermissionsCache();
          setLoading(false);
        } else if (e.oldValue !== e.newValue) {
          // Token modifié = nouvelle connexion
          console.log('🔄 Nouveau token détecté, rechargement des permissions');
          loadUserPermissions(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadUserPermissions, clearPermissionsCache]);

  useEffect(() => {
    loadUserPermissions();
  }, [loadUserPermissions]);

  // ✅ Autres fonctions utilitaires
  const hasAnyPermission = useCallback((modules) => {
    return modules.some(module => hasPermission(module));
  }, [hasPermission]);

  const hasAnyGranularPermission = useCallback((permissionsArray) => {
    return permissionsArray.some(({ module, sousModule, action }) => 
      hasGranularPermission(module, sousModule, action)
    );
  }, [hasGranularPermission]);

  const isPermissionDisabled = useCallback((module, sousModule, action) => {
    return !hasGranularPermission(module, sousModule, action);
  }, [hasGranularPermission]);

  const reloadPermissions = useCallback(async () => {
    console.log('🔄 Rechargement manuel des permissions...');
    setLoading(true);
    await loadUserPermissions(true);
  }, [loadUserPermissions]);

  const handleLogout = useCallback(() => {
    console.log('👋 Déconnexion: nettoyage complet');
    setModulePermissions(new Set());
    setDetailedPermissions([]);
    clearPermissionsCache();
    setError(null);
    setLastUpdate(null);
    permissionsPromise = null;
  }, [clearPermissionsCache]);

  const value = {
    modulePermissions,
    detailedPermissions,
    loading,
    error,
    lastUpdate,
    hasPermission,
    hasGranularPermission,
    hasAnyPermission,
    hasAnyGranularPermission,
    isPermissionDisabled,
    reloadPermissions,
    refreshPermissions,
    handleLogout,
    clearPermissionsCache,
    debugUserPermissions, // ✅ NOUVEAU : Pour déboguer
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};